import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAssistStream } from "./AppAssistStream";

// AppAssistWarm - WHEN the owner's browser tells the service to have Claude ready (`/assist {action: "warm"}`).
//
// ⭐ APP START, KEPT ALIVE BY VISIBLE TABS (owner, 2026-09-27, and the warm-process evaluation of the same day):
// the first warm-up goes as soon as `/folder` says this browser is the owner's - whatever route is open - so
// Claude is ready before a contact is even picked. After that a warm-up goes on a contact open (it also names
// the page, so the service reads that chat ahead), on the AI panel opening, when the tab becomes visible and
// every `EveryMs` while it IS visible. A hidden tab sends nothing, and the service lets its process go ten
// minutes after the last warm-up or question.
// ⚠ Warm-ups never cost a question: the service has its own throttle for them (one per number per 5 s, never
// the 30-per-15-minutes or daily budget) and never starts a second idle process. `MinGapMs` here only avoids
// sending what the service would drop anyway.
// ⛔ A warm-up asks nothing and sends nothing - the answer is ignored.

/** How a warm-up body is posted (the host's `/assist` transport); the answer is not read. */
export type AppAssistWarmPost = (Body: string) => void;

export class AppAssistWarmRules {
    /** While a tab is visible, one keep-alive this often (under the service's ten-minute idle window). */
    public static readonly EveryMs: number = 240000;
    /** Never two warm-ups closer than this (the service's per-number gap is 5 s). */
    public static readonly MinGapMs: number = 5000;

    /** Whether an event-driven warm-up (start, contact, panel, tab shown) goes now. DOCUMENTED EXCEPTION: pure. */
    public static Due = (Visible: boolean, LastMs: number, NowMs: number): boolean => {
        return Visible === true && (LastMs <= 0 || NowMs - LastMs >= AppAssistWarmRules.MinGapMs);
    };

    /** Whether the keep-alive tick sends one. DOCUMENTED EXCEPTION: pure. */
    public static TickDue = (Visible: boolean, LastMs: number, NowMs: number): boolean => {
        return Visible === true && LastMs > 0 && NowMs - LastMs >= AppAssistWarmRules.EveryMs - 1000;
    };
}

/** The page's warm-up sender - one per page. ⚠ Only the owner's browser calls it (`AppMain.IsOwner`). */
export class AppAssistWarmer {
    private Post: AppAssistWarmPost;
    private LastMs: number = 0;
    /** Warm-ups sent - for the tests. */
    public Sent: number = 0;

    public constructor(Post: AppAssistWarmPost) {
        this.Post = Post;
    }

    /** An event-driven warm-up: app start (`Page` ""), a contact open (its href), the panel, the tab shown. */
    public Ping = (Phone: string, Key: string, Page: string, Visible: boolean, NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppAssistWarmRules.Due(Visible, this.LastMs, NowMs) !== true) { return false; }
            return this.Go(Phone, Key, Page, NowMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistWarmer.Ping");
            return false;
        }
    };

    /** The keep-alive tick: only from a visible tab, and only once warm-ups have started. */
    public Tick = (Phone: string, Key: string, Page: string, Visible: boolean, NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppAssistWarmRules.TickDue(Visible, this.LastMs, NowMs) !== true) { return false; }
            return this.Go(Phone, Key, Page, NowMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistWarmer.Tick");
            return false;
        }
    };

    private Go = (Phone: string, Key: string, Page: string, NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Body: string = AppAssistStream.WarmBody(Phone, Key, nz(Page));
            if (Body.length === 0) { return false; }
            this.LastMs = NowMs;
            this.Sent = this.Sent + 1;
            this.Post(Body);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistWarmer.Go");
            return false;
        }
    };
}
